/**
 * Telegram Mini App 런타임 접근
 *
 * 채팅 밖(일반 브라우저)에서는 window.Telegram 이 없다.
 * 그때는 isTelegram=false 를 돌려주고 나머지는 no-op 이라,
 * 같은 컴포넌트가 웹에서도 그대로 동작한다.
 */
import { useEffect, useState } from 'react';

export const tg = () => window.Telegram?.WebApp;

/** 채팅 안에서 열렸는지. initData 가 있어야 진짜 Mini App 이다. */
export const isTelegramWebApp = () => Boolean(tg()?.initData);

export const useTelegram = () => {
    const webApp = tg();
    const isTelegram = isTelegramWebApp();

    useEffect(() => {
        if (!webApp) return;
        try {
            webApp.ready();
            webApp.expand();
            // 아래로 당겨서 닫히는 제스처는 긴 리포트를 스크롤할 때 오작동한다
            webApp.disableVerticalSwipes?.();
        } catch (e) {
            // 구버전 클라이언트는 일부 메서드가 없다. 표시에는 지장이 없다.
        }
    }, [webApp]);

    return { webApp, isTelegram };
};

/**
 * 네이티브 뒤로가기 버튼.
 * 텔레그램 헤더에 표시되므로 페이지 안에 버튼을 그릴 필요가 없다.
 */
export const useBackButton = (onClick) => {
    useEffect(() => {
        const webApp = tg();
        if (!webApp?.BackButton || !onClick) return;
        webApp.BackButton.onClick(onClick);
        webApp.BackButton.show();
        return () => {
            webApp.BackButton.offClick(onClick);
            webApp.BackButton.hide();
        };
    }, [onClick]);
};

/** 햅틱. 지원하지 않으면 조용히 넘어간다. */
export const haptic = (style = 'light') => {
    try {
        tg()?.HapticFeedback?.impactOccurred(style);
    } catch (e) {
        /* no-op */
    }
};

/**
 * 텔레그램 테마를 CSS 변수로 노출합니다.
 *
 * 사용자가 앱에서 테마를 바꾸면 themeChanged 이벤트가 온다.
 * 이걸 반영해야 웹페이지가 아니라 앱의 일부로 읽힌다.
 */
export const useTelegramTheme = () => {
    const [theme, setTheme] = useState(() => tg()?.themeParams || {});

    useEffect(() => {
        const webApp = tg();
        if (!webApp) return;

        const apply = () => {
            const p = webApp.themeParams || {};
            setTheme(p);
            const root = document.documentElement;
            const map = {
                '--tg-bg': p.bg_color,
                '--tg-text': p.text_color,
                '--tg-hint': p.hint_color,
                '--tg-link': p.link_color,
                '--tg-button': p.button_color,
                '--tg-button-text': p.button_text_color,
                '--tg-secondary-bg': p.secondary_bg_color,
                '--tg-section-bg': p.section_bg_color || p.bg_color,
                '--tg-separator': p.section_separator_color || p.hint_color,
            };
            Object.entries(map).forEach(([k, v]) => v && root.style.setProperty(k, v));
            root.dataset.tgTheme = webApp.colorScheme || 'light';
        };

        apply();
        webApp.onEvent?.('themeChanged', apply);
        return () => webApp.offEvent?.('themeChanged', apply);
    }, []);

    return theme;
};
